import React, { useState } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Lock,
  CheckCircle2,
  FileText,
  Activity,
  Layers,
  Search,
  RefreshCw
} from 'lucide-react';
import { AnalyticsPrivacyViolationItem } from '../../types';
import { analyticsEngine } from '../../services/analyticsEngine';

export const AnalyticsPrivacyQualityHub: React.FC = () => {
  const [violations] = useState<AnalyticsPrivacyViolationItem[]>(analyticsEngine.getPrivacyViolations());
  const telemetry = analyticsEngine.getTelemetry();

  const finPrivacyCheck = analyticsEngine.runFinancialValuesMustNotReachGA4Check();

  return (
    <div className="space-y-8 pb-12">
      {/* Hero Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-500/30">
          <ShieldCheck className="w-3.5 h-3.5" />
          Analytics Privacy Scrubber & Data Quality Center
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Privacy Scrubber & Data Quality Observability</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Automated privacy interceptor blocking raw financial values and PII from reaching GA4 or third-party tracking destinations.
        </p>
      </div>

      {/* Primary Status Banner */}
      <div className="bg-emerald-950/90 text-white p-6 rounded-2xl border border-emerald-800 shadow-md space-y-3">
        <div className="flex items-center justify-between border-b border-emerald-800 pb-2">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-emerald-400" />
            <h3 className="font-bold text-sm">Automated Financial Values Privacy Control</h3>
          </div>
          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-xs font-mono font-bold">
            100% Fail-Safe Active
          </span>
        </div>
        <p className="text-emerald-100 text-xs leading-relaxed">
          {finPrivacyCheck.message}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Blocked Prohibited Properties</span>
            <ShieldAlert className="w-4 h-4 text-purple-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            {telemetry.privacyScrubberViolationsBlockedCount}
          </div>
          <div className="text-[11px] text-purple-700 font-semibold">
            Interception Active at Collector
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>GA4 Sync Health</span>
            <Activity className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-emerald-700 font-mono">
            {telemetry.ga4SyncHealthStatus}
          </div>
          <div className="text-[11px] text-slate-500">
            Metadata Only (No PII)
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>First-Party Warehouse</span>
            <RefreshCw className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-black text-blue-700 font-mono">
            {telemetry.firstPartyWarehouseStatus}
          </div>
          <div className="text-[11px] text-slate-500">
            Event Store & Rollups Operational
          </div>
        </div>
      </div>

      {/* Intercepted Violations Audit Log Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <div className="text-xs font-bold text-purple-600 uppercase tracking-wider">
              Scrubber Audit Trail
            </div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">
              Intercepted Privacy Violations Log
            </h2>
          </div>

          <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-bold">
            Zero PII Exported
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-bold uppercase text-[10px]">
                <th className="py-2.5 px-3">Timestamp</th>
                <th className="py-2.5 px-3">Event Name</th>
                <th className="py-2.5 px-3">Prohibited Property Intercepted</th>
                <th className="py-2.5 px-3">Attempted Destination</th>
                <th className="py-2.5 px-3">Action Taken</th>
                <th className="py-2.5 px-3">Severity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-mono text-slate-700">
              {violations.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-2.5 px-3 text-slate-500 text-[11px]">
                    {new Date(v.timestamp).toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3 font-bold text-slate-900">{v.eventName}</td>
                  <td className="py-2.5 px-3 font-bold text-red-600">{v.prohibitedPropertyDetected}</td>
                  <td className="py-2.5 px-3 font-bold text-blue-600">{v.attemptedDestination}</td>
                  <td className="py-2.5 px-3">
                    <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                      {v.actionTaken}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      v.incidentSeverity === 'CRITICAL' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {v.incidentSeverity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
